"use client";

import { Slot } from "@radix-ui/react-slot";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import * as React from "react";

import {
  type RecipeVariantProps,
  css,
  cva,
  cx,
  sva,
} from "@/../styled-system/css";
import HamburgerMenu from "@/assets/svg/menu.svg";
import Button from "@/components/ui/Button";
import Skeleton from "@/components/ui/Skeleton";
import { TooltipProvider } from "@/components/ui/Tooltip";

import Input from "./Input";
import { Separator } from "./Separator";
import { Sheet, SheetContent, SheetTitle } from "./Sheet";

const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

const sidebar = sva({
  slots: [
    "provider",
    "root",
    "trigger",
    "rail",
    "input",
    "header",
    "inset",
    "footer",
    "separator",
    "content",
    "group",
    "groupLabel",
    "groupAction",
    "groupContent",
    "menu",
    "menuItem",
    "menuAction",
    "badge",
    "skeleton",
    "menuSub",
  ],
  base: {
    provider: {
      display: "flex",
      w: "full",
      minHeight: "100svh",
      "&:has([data-variant=inset]) &": {
        bg: "red",
      },
    },
    root: {
      display: "flex",
      h: "full",
      w: "var(--sidebar-width)",
      flexDir: "column",
      bg: "brandBastille",
      color: "white",
    },
    trigger: {
      h: "28px",
      w: "28px",
      px: "0",
      py: "0",
      color: "black",
      rounded: "sm",
    },
    rail: {
      pos: "absolute",
      top: "0",
      bottom: "0",
      zIndex: "20",
      display: "none",
      w: "20px",
      translateX: "50%",
      transitionProperty: "all",
      transitionTimingFunction: "linear",
      transitionDuration: "150ms",
      _after: {
        content: "var(--tw-content)",
        pos: "absolute",
        top: "0",
        bottom: "0",
        left: "50%",
        w: "2px",
        _hover: {
          bgColor: "green",
        },
        '.group:is([data-collapsible="offcanvas"]) &': {
          _hover: { bgColor: "green" },
          left: "100%",
        },
      },
      '.group:is([data-side="left"]) &': { right: "20px" },
      '.group:is([data-side="right"]) &': { left: "0" },
      sm: { display: "flex" },
      '.group:is([data-collapsible="offcanvas"]) &': {
        translateX: "0",
      },
      "_&": {
        "&[data-side=left]": { cursor: "w-resize" },
        "&[data-side=right]": { cursor: "e-resize" },
        "&[data-state=collapsed]": {
          "&[data-side=left]": { cursor: "e-resize" },
          "&[data-side=right]": { cursor: "w-resize" },
        },
        "&[data-state=offcanvas]": {
          "&[data-side=left]": { left: "8px" },
          "&[data-side=right]": { right: "8px" },
        },
      },
    },
    input: {
      h: "32px",
      w: "full",
      shadow: "none",
      _focusVisible: { ringOffset: "2", ringWidth: "2", ringColor: "gray" },
    },
    header: {
      display: "flex",
      flexDirection: "row",
      gap: "2",
      p: "4",
      minHeight: "50px",
      alignItems: "center",
      justifyContent: "space-between",
    },
    inset: {
      pos: "relative",
      display: "flex",
      minHeight: "100svh",
      flex: "1",
      flexDir: "column",
      backgroundColor: "yellow",
      '.peer:is([data-variant="inset"]) &': {
        minH: "calc(100svh-token(spacing.4))",
        md: { m: "2", ml: "0", rounded: "xl", boxShadow: "sm" },
        '.peer:is([data-state="collapsed"]) &': { ml: "2" },
      },
    },
    footer: {
      display: "flex",
      flexDirection: "row",
      gap: "3",
      p: "4",
      whiteSpace: "nowrap",
      alignItems: "center",
    },
    separator: {
      mx: "2",
      w: "auto",
      backgroundColor: "green",
    },
    content: {
      display: "flex",
      minH: "0",
      flex: "1",
      flexDir: "column",
      gap: "2",
      overflow: "auto",
      '.group:is([data-collapsible="icon"]) &': { overflow: "hidden" },
    },
    group: {
      pos: "relative",
      display: "flex",
      w: "full",
      minW: "0",
      flexDir: "column",
      p: "2",
    },
    groupLabel: {
      transitionDuration: "200ms",
      display: "flex",
      h: "32px",
      flexShrink: "0",
      alignItems: "center",
      rounded: "md",
      px: "2",
      fontSize: "xs",
      lineHeight: "xs",
      fontWeight: "medium",
      color: "white",
      ringWidth: "unset",
      ringOffset: "none",
      transitionProperty: "margin,opa",
      transitionTimingFunction: "linear",
      _focusVisible: { ringWidth: "2px", ringColor: "red", ringOffset: "0.5" },
      "& > svg": { flexShrink: "0", width: "16px", height: "16px" },
      '.group:is([data-collapsible="icon"]) &': { mt: "8", opacity: "0" },
    },
    groupAction: {
      pos: "absolute",
      right: "12px",
      top: "14px",
      display: "flex",
      aspectRatio: "square",
      w: "20px",
      alignItems: "center",
      justifyContent: "center",
      rounded: "md",
      p: "0",
      color: "red",
      ring: "none",
      ringOffset: "none",
      transitionProperty: "transform",
      transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
      transitionDuration: "150ms",
      _hover: {
        backgroundColor: "purple",
        color: "rose.500",
      },
      _focusVisible: { ringWidth: "2", ringColor: "gray", ringOffset: "2" },
      "& > svg": { flexShrink: "0" },
      _after: {
        pos: "absolute",
        inset: "8px",
        md: {
          display: "none",
        },
      },
      '.group:is([data-collapsible="icon"]) &': { display: "none" },
    },
    groupContent: {
      w: "full",
      fontSize: "sm",
      lineHeight: "sm",
    },
    menu: {
      display: "flex",
      w: "full",
      minW: "0",
      flexDir: "column",
      gap: "1",
    },
    menuItem: {
      pos: "relative",
    },
    menuAction: {
      pos: "absolute",
      right: "4px",
      top: "6px",
      display: "flex",
      aspectRatio: "square",
      w: "20px",
      alignItems: "center",
      justifyContent: "center",
      rounded: "md",
      p: "0",
      color: "rose.500",
      ring: "none",
      ringOffset: "none",
      transitionProperty: "transform",
      transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
      transitionDuration: "150ms",
      _hover: {
        backgroundColor: "purple",
        color: "rose.500",
      },
      _focusVisible: {
        outlineOffset: "2px",
        ringWidth: "2",
        ringOffset: "2",
        ringColor: "gray",
      },
      ".peer:is(:hover, [data-hover]) ~ .menu-button &": {
        color: "rose.500",
      },
      "& > svg": { flexShrink: "0", width: "16px", height: "16px" },
      _after: {
        pos: "absolute",
        inset: "8px",
        md: { display: "none" },
      },
      '.peer:is([data-size="lg"]) ~ .menu-button &': {
        top: "10px",
      },
      '.peer:is([data-size="default"]) ~ .menu-button &': {
        top: "6px",
      },
      '.peer:is([data-size="sm"]) ~ .menu-button &': {
        top: "4px",
      },
      '.group:is([data-collapsible="icon"]) &': {
        display: "none",
      },
    },
    badge: {
      pos: "absolute",
      right: "4px",
      display: "flex",
      h: "20px",
      minWidth: "20px",
      alignItems: "center",
      justifyContent: "center",
      rounded: "md",
      px: "1",
      fontSize: "xs",
      lineHeight: "xs",
      fontWeight: "medium",
      fontVariantNumeric: "tabular-nums",
      color: "red",
      userSelect: "none",
      pointerEvents: "none",
      ".peer:is(:hover, [data-hover]) ~ .menu-button &": {
        color: "rose.500",
      },
      '.peer:is(:active, [data-active="true"]) ~ .menu-button &': {
        color: "rose.500",
      },
      '.peer:is([data-size="lg"]) ~ .menu-button &': {
        top: "10px",
      },
      '.peer:is([data-size="default"]) ~ .menu-button &': {
        top: "6px",
      },
      '.peer:is([data-size="sm"]) ~ .menu-button &': {
        top: "4px",
      },
      '.group:is([data-collapsible="icon"]) &': {
        display: "none",
      },
    },
    skeleton: {
      rounded: "md",
      h: "32px",
      display: "flex",
      gap: "2",
      px: "2",
      alignItems: "center",
    },
    menuSub: {
      mx: "3.5",
      display: "flex",
      minW: "0",
      transform: "translateX(1px)",
      flexDir: "column",
      gap: "1",
      px: "2.5",
      py: "0.5",
      '.group:is([data-collapsible="icon"]) &': {
        display: "none",
      },
    },
  },
});

const {
  provider,
  root,
  trigger,
  rail,
  input,
  header,
  inset,
  footer,
  separator,
  content,
  group,
  groupLabel,
  groupAction,
  groupContent,
  menu,
  menuItem,
  badge,
  skeleton,
  menuSub,
} = sidebar.raw();

type SidebarContextProps = {
  state: "expanded" | "collapsed";
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    css?: object;
    style?: React.CSSProperties;
  }
>(({ css: cssProp = {}, style, children, ...props }, ref) => {
  const className = cx("group sidebar-wrapper", css(provider, cssProp));
  const [isOpen, setIsOpen] = React.useState(false);

  // Helper to toggle the sidebar
  const toggleSidebar = React.useCallback(() => {
    return setIsOpen((current) => !current);
  }, [setIsOpen]);

  // Keyboard shortcut to toggle the sidebar
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // State for easier styling
  const state = isOpen ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      isOpen,
      setIsOpen,
      toggleSidebar,
    }),
    [state, isOpen, setIsOpen, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={className}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
});

SidebarProvider.displayName = "SidebarProvider";

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    side?: "left" | "right";
    collapsible?: "offcanvas" | "icon" | "none";
    css?: object;
  }
>(
  (
    {
      side = "left",
      collapsible = "offcanvas",
      css: cssProp = {},
      children,
      ...props
    },
    ref,
  ) => {
    const { isOpen, setIsOpen } = useSidebar();
    const className = css(root, cssProp);

    if (collapsible === "none") {
      return (
        <div className={className} ref={ref} {...props}>
          {children}
        </div>
      );
    }

    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen} {...props}>
        <VisuallyHidden asChild>
          <SheetTitle />
        </VisuallyHidden>

        <SheetTitle className={css({ display: "none" })}></SheetTitle>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          css={{
            w: "var(--sidebar-width)",
            p: "0",
            bgColor: "brandBastille",
            color: "white",
            "& > button": { display: "none" },
          }}
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
          aria-describedby={undefined}
        >
          <div
            className={css({
              display: "flex",
              h: "full",
              w: "full",
              flexDir: "column",
            })}
          >
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  },
);
Sidebar.displayName = "Sidebar";

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button> & {
    css?: object;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  }
>(({ css: cssProp = {}, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();
  const className = css.raw(trigger, cssProp);

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      width="icon"
      css={className}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <HamburgerMenu width="24px" />
      <span
        className={css({
          srOnly: "true",
        })}
      >
        Open Sidebar
      </span>
    </Button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();
  const className = css(rail, cssProp);

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={className}
      {...props}
    />
  );
});
SidebarRail.displayName = "SidebarRail";

const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"main"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(inset, cssProp);
  return <main ref={ref} className={className} {...props} />;
});
SidebarInset.displayName = "SidebarInset";

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(input, cssProp);
  return (
    <Input ref={ref} data-sidebar="input" className={className} {...props} />
  );
});
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(header, cssProp);
  return (
    <div ref={ref} data-sidebar="header" className={className} {...props} />
  );
});
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(footer, cssProp);
  return (
    <div ref={ref} data-sidebar="footer" className={className} {...props} />
  );
});
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(separator, cssProp);
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={className}
      {...props}
    />
  );
});
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(content, cssProp);
  return (
    <div ref={ref} data-sidebar="content" className={className} {...props} />
  );
});
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(group, cssProp);
  return (
    <div ref={ref} data-sidebar="group" className={className} {...props} />
  );
});
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { asChild?: boolean; css?: object }
>(({ css: cssProp = {}, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "div";
  const className = css(groupLabel, cssProp);
  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={className}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { asChild?: boolean; css?: object }
>(({ css: cssProp = {}, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  const className = css(groupAction, cssProp);
  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={className}
      {...props}
    />
  );
});
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(groupContent, cssProp);
  return (
    <div
      ref={ref}
      data-sidebar="group-content"
      className={className}
      {...props}
    />
  );
});
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(menu, cssProp);
  return <ul ref={ref} data-sidebar="menu" className={className} {...props} />;
});
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li"> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = cx("group menu-item", css(menuItem, cssProp));
  return (
    <li ref={ref} data-sidebar="menu-item" className={className} {...props} />
  );
});
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva({
  base: {
    display: "flex",
    w: "full",
    alignItems: "center",
    gap: "2",
    overflow: "hidden",
    rounded: "md",
    p: "2",
    textAlign: "left",
    fontSize: "sm",
    lineHeight: "sm",
    ringWidth: "unset",
    ringOffset: "none",
    transitionProperty: "width,height,padding",
    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
    transitionDuration: "150ms",
    _hover: {
      color: "white",
      backgroundColor: "brandBastille/70",
    },
    '.group:has([data-sidebar="menu-action"]) .menu-item &': {
      pr: "8",
    },
    _focusVisible: {
      ringWidth: "2px",
      ringOffset: "2px",
      ringColor: "gray",
    },
    _disabled: { pointerEvents: "none", opacity: "0.5" },
    "&[data-state=open]": {
      _hover: {
        color: "white",
        backgroundColor: "gray.600/50",
      },
    },
    "&[data-active=true]": {
      fontWeight: "medium",
      color: "white",
      backgroundColor: "gray.600/50",
    },
    '.group:is([data-collapsible="icon"]) &': {
      p: "2!",
      width: "32px!",
      height: "32px!",
    },
    "& > span:last-child": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& > svg": { flexShrink: "0", width: "16px", height: "16px" },
  },
  variants: {
    variant: {
      default: {
        _hover: {
          color: "white",
          backgroundColor: "gray.600/50",
        },
      },
      outline: {
        backgroundColor: "yellow",
        shadow: "0 0 0 1px green",
        _hover: {
          shadow: "0 0 0 1px purple",
          color: "rose.500",
          backgroundColor: "purple",
        },
      },
    },
    size: {
      default: { h: "32px", fontSize: "sm", lineHeight: "sm" },
      sm: { h: "28px", fontSize: "xs", lineHeight: "xs" },
      lg: {
        h: "48px",
        fontSize: "sm",
        lineHeight: "sm",
        '.group:is([data-collapsible="icon"]) &': { p: "0!" },
      },
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    css?: object;
  } & RecipeVariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = "default",
      size = "default",
      css: cssProp = {},
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const className = cx(
      "peer menu-button",
      css(
        sidebarMenuButtonVariants.raw({ variant: variant, size: size }),
        cssProp,
      ),
    );

    return (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={className}
        {...props}
      />
    );
  },
);
SidebarMenuButton.displayName = "SidebarMenuButton";

const sidebarMenuActionVariants = cva({
  base: {
    pos: "absolute",
    right: "4px",
    top: "6px",
    display: "flex",
    aspectRatio: "square",
    w: "20px",
    alignItems: "center",
    justifyContent: "center",
    rounded: "md",
    p: "0",
    color: "rose.500",
    ring: "none",
    ringOffset: "none",
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
    transitionDuration: "150ms",
    _hover: {
      backgroundColor: "purple",
      color: "rose.500",
    },
    _focusVisible: {
      outlineOffset: "2px",
      ringWidth: "2",
      ringOffset: "2",
      ringColor: "gray",
    },
    ".peer:is(:hover, [data-hover]) ~ .menu-button &": {
      color: "rose.500",
    },
    "& > svg": { flexShrink: "0", width: "16px", height: "16px" },
    _after: {
      pos: "absolute",
      inset: "8px",
      md: { display: "none" },
    },
    '.peer:is([data-size="lg"]) ~ .menu-button &': {
      top: "10px",
    },
    '.peer:is([data-size="default"]) ~ .menu-button &': {
      top: "6px",
    },
    '.peer:is([data-size="sm"]) ~ .menu-button &': {
      top: "4px",
    },
    '.group:is([data-collapsible="icon"]) &': {
      display: "none",
    },
  },
  variants: {
    showOnHover: {
      true: {
        ".group:is(:focus-visible, [data-focus-visible]) .menu-item &": {
          opacity: "100",
        },
        ".group:is(:hover, [data-hover]) .menu-item &": {
          opacity: "100",
        },
        "&[data-state=open]": {
          opacity: "100",
        },
        '.peer:is(:active, [data-active="true"]) ~ .menu-button &': {
          color: "rose.500",
        },
        md: { opacity: "0" },
      },
    },
  },
});

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    showOnHover?: boolean;
    css?: object;
  }
>(
  (
    { css: cssProp = {}, asChild = false, showOnHover = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const className = css(
      sidebarMenuActionVariants.raw({ showOnHover: showOnHover }),
      cssProp,
    );

    return (
      <Comp
        ref={ref}
        data-sidebar="menu-action"
        className={className}
        {...props}
      />
    );
  },
);
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(badge, cssProp);

  return (
    <div ref={ref} data-sidebar="menu-badge" className={className} {...props} />
  );
});
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    showIcon?: boolean;
    css?: object;
  }
>(({ css: cssProp = {}, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  const className = css(skeleton, cssProp);
  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={className}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className={css({ rounded: "md", width: "16px", height: "16px" })}
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className={css({ h: "16px", flex: "1", maxW: "var(--skeleton-width)" })}
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul"> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(menuSub, cssProp);

  return (
    <ul ref={ref} data-sidebar="menu-sub" className={className} {...props} />
  );
});
SidebarMenuSub.displayName = "SidebarMenuSub";

const sidebarMenuSubButton = cva({
  base: {
    display: "flex",
    h: "28px",
    minW: "0",
    transform: "translateX(1px)",
    alignItems: "center",
    gap: "2",
    overflow: "hidden",
    rounded: "md",
    px: "2",
    ring: "none",
    ringOffset: "none",
    _focusVisible: {
      outlineOffset: "2px",
      ringWidth: "2",
      ringOffset: "2",
      ringColor: "gray",
    },
    _hover: {
      color: "white",
      backgroundColor: "gray.600/50",
    },
    "&[data-active=true]": {
      color: "white",
      backgroundColor: "gray.600/50",
    },
    _disabled: { pointerEvents: "none", opacity: "0.5" },
    "& > span:last-child": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& > svg": {
      flexShrink: "0",
      width: "16px",
      height: "16px",
    },
    '.group:is([data-collapsible="offcanvas"]) &': { display: "none" },
  },
  variants: {
    size: {
      sm: { fontSize: "xs", lineHeight: "xs" },
      md: {
        fontSize: "sm",
        lineHeight: "sm",
      },
    },
  },
});

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ ...props }, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<"a"> & {
    asChild?: boolean;
    size?: "sm" | "md";
    isActive?: boolean;
    css?: object;
  }
>(
  (
    { asChild = false, size = "md", isActive, css: cssProp = {}, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "a";
    const className = css(sidebarMenuSubButton.raw({ size: size }), cssProp);

    return (
      <Comp
        ref={ref}
        data-sidebar="menu-sub-button"
        data-size={size}
        data-active={isActive}
        className={className}
        {...props}
      />
    );
  },
);
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
